import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Store } from "@ngxs/store";
import { LayoutBreakPointsService } from "@shared/services/layout-break-points.service";
import { Chart } from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";

@Component({
  selector: "app-groups-chart",
  templateUrl: "./groups-chart.component.html",
  styleUrls: ["./groups-chart.component.scss"],
  standalone: true,
  imports: [],
})
export class GroupsChartComponent implements OnInit, OnChanges, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input() public chartId: string;
  @Input() public refreshGraph;

  private myGroupedChart: any;
  private yearlyGwp;
  private motor = [];
  private nonMotor = [];
  private year = [];
  private totalValue = [];
  private motorValueInThousands = [];
  private nonMotorValueInThousands = [];
  private totalValueInThousands = [];
  private readonly convertMotorValueInThousands: any;
  private readonly gwpData: any;
  protected desktopView: boolean = false;
  private totalSum: number = 0;
  protected year1Desktop: string = "";
  protected year2Desktop: string = "";
  protected year1Mobile: string = "";
  protected year2Mobile: string = "";

  constructor(
    private readonly layoutBreakpointService: LayoutBreakPointsService,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.getYearlyGwp();
    this.layoutBreakpointService
      .subscribeLayoutChanges()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.desktopView = this.layoutBreakpointService.renderDesktop();
      });
  }

  ngOnChanges(): void {
    if (this.refreshGraph === true) {
      if (this.myGroupedChart != null) {
        this.myGroupedChart.destroy();
        this.getYearlyGwp();
        if (this.chartId) {
          this.createGroupedChart();
        }
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.chartId) {
      if (this.myGroupedChart) {
        this.myGroupedChart.destroy();
        this.myGroupedChart = undefined;
      }
      this.createGroupedChart();
    }
  }

  private getTotalSum(): void {
    function round(value, precision) {
      const multiplier = Math.pow(10, precision || 0);
      return Math.round(value * multiplier) / multiplier;
    }
    this.yearlyGwp.forEach((i) => {
      this.totalSum += i.motorValue + i.nonMotorValue;
    });
    this.totalSum = round(this.totalSum / 1000, 0);
  }

  private getYearlyGwp(): void {
    this.motor = [];
    this.nonMotor = [];
    this.year = [];
    this.totalValue = [];
    this.motorValueInThousands = [];
    this.totalValueInThousands = [];
    this.nonMotorValueInThousands = [];

    this.yearlyGwp = this.store.selectSnapshot<any>(
      (state) => state.UserPerformanceState.gwpChartData.yearlyGwp,
    );

    const reversedGwp = [];
    reversedGwp.push(this.yearlyGwp[1]);
    reversedGwp.push(this.yearlyGwp[0]);

    reversedGwp.forEach((value) => {
      this.motor.push(value.motorValue);
      this.nonMotor.push(value.nonMotorValue);
      this.year.push(value.year);
      this.totalValue.push(value.totalValue);
    });

    function round(value, precision) {
      const multiplier = Math.pow(10, precision || 0);
      return Math.round(value * multiplier) / multiplier;
    }

    this.motor.forEach((i) => {
      if (i / 1000 < 1) {
        this.motorValueInThousands.push(round(i / 1000, 1));
      } else {
        this.motorValueInThousands.push(round(i / 1000, 0));
      }
    });

    this.nonMotor.forEach((i) => {
      if (i / 1000 < 1) {
        this.nonMotorValueInThousands.push(round(i / 1000, 1));
      } else {
        this.nonMotorValueInThousands.push(round(i / 1000, 0));
      }
    });

    this.totalValue.forEach((i) => {
      if (i / 1000 < 1) {
        this.totalValueInThousands.push(round(i / 1000, 1));
      } else {
        this.totalValueInThousands.push(round(i / 1000, 0));
      }
    });

    const formatLabel = (value) =>
      value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    this.year1Desktop = `${this.year[0]}: RM ${formatLabel(
      this.motorValueInThousands[0] + this.nonMotorValueInThousands[0],
    )}K`;
    this.year2Desktop = `${this.year[1]}: RM ${formatLabel(
      this.motorValueInThousands[1] + this.nonMotorValueInThousands[1],
    )}K`;
    this.year1Mobile = `${this.year[0]}:\r\n RM ${formatLabel(
      this.motorValueInThousands[0] + this.nonMotorValueInThousands[0],
    )}K`;
    this.year2Mobile = `${this.year[1]}:\r\n RM ${formatLabel(
      this.motorValueInThousands[1] + this.nonMotorValueInThousands[1],
    )}K`;

    this.getTotalSum();
  }

  private createGroupedChart(): void {
    const staticValue = {
      borderWidth: 1,
      yAxesGroup: true,
      xAxisId: this.chartId,
      borderColor: "#b8b8b8",
      backgroundColor1: ["#C1EBFB", "#007AB3", "#96DCFA"],
      backgroundColor2: ["#96DCFA", "#003781", "#003781"],
    };
    const yScaleLabel = {
      id: "yScaleLabel",
      afterDatasetsDraw(chart, _args, _options) {
        const {
          ctx,
          chartArea: { top },
        } = chart;

        const arr = chart.data.datasets.flatMap((item) =>
          item.data.map((val) => val),
        );
        let hideText = false;
        if (arr.includes(0)) {
          hideText = true;
        }

        ctx.save();
        ctx.font = "bold 12px Helvetica Neue, Helvetica, Arial, sans-serif";
        ctx.padding = "50px";
        if (!hideText) {
          ctx.fillText("RM (K)", 38, top - 20);
        }
        ctx.restore();
      },
    };
    const ctx = document.getElementById(this.chartId) as HTMLCanvasElement;
    const groupedChart = new Chart(ctx, {
      type: "bar",
      plugins: [ChartDataLabels, yScaleLabel],
      data: {
        datasets: [
          {
            stack: "motor",
            label: "motor",
            data: this.motorValueInThousands,
            backgroundColor: staticValue.backgroundColor1,
            borderWidth: staticValue.borderWidth,
            borderColor: staticValue.borderColor,
            fill: true,
            categoryPercentage: 1.0,
            datalabels: {
              font: () => {
                return {
                  family: "Allianz Neo , Arial, sans-serif",
                };
              },
              labels: {
                index: {
                  align: "end",
                  anchor: "end",
                  offset: 0,
                  color: (value) => this.setDataLabelColor(value),
                  formatter: (value) => {
                    return value !== 0
                      ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : "";
                  },
                },
                name: {
                  align: "start",
                  anchor: "start",
                  offset: 2,
                  formatter: () => {
                    return "Motor";
                  },
                },
              },
            },
          },
          {
            stack: "non-motor",
            label: "non-motor",
            data: this.nonMotorValueInThousands,
            backgroundColor: staticValue.backgroundColor2,
            borderWidth: staticValue.borderWidth,
            borderColor: staticValue.borderColor,
            fill: true,
            categoryPercentage: 1.0,
            datalabels: {
              font: () => {
                return {
                  family: "Allianz Neo , Arial, sans-serif",
                };
              },
              labels: {
                index: {
                  align: "end",
                  anchor: "end",
                  offset: 0,
                  color: (value) => this.setDataLabelColor(value),
                  formatter: (value) => {
                    return value !== 0
                      ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : "";
                  },
                },
                name: {
                  align: "start",
                  anchor: "start",
                  offset: 2,
                  formatter: () => {
                    return "Non-Motor";
                  },
                },
              },
            },
          },
        ],
      },

      options: {
        scales: {
          x: {
            labels: ["", ""],
            grid: {
              drawOnChartArea: false,
              drawTicks: false,
            },
            ticks: {
              color: () => {
                return "#414141";
              },
              font: () => {
                return {
                  size: this.desktopView ? 14 : 12,
                  family: "Allianz Neo , Arial, sans-serif",
                  // weight: 'bold',
                  lineHeight: this.desktopView ? "25px" : "20px",
                };
              },
              padding: this.desktopView ? 20 : 23,
            },
          },

          y: {
            display: true,
            stacked: true,
            grid: {
              drawOnChartArea: false,
            },
            ticks: {
              callback: (value, _index, _ticks) => {
                return Intl.NumberFormat().format(Number(value));
              },
              font: () => {
                return {
                  // weight: '400',
                  // family: 'Allianz Neo , Arial, sans-serif',
                };
              },
              padding: 0,
            },
          },
        },
        maintainAspectRatio: false,
        responsive: true,
        layout: {
          padding: {
            left: 8,
            right: 3,
            top: 35,
            bottom: this.desktopView ? 7 : 0,
          },
        },
        plugins: {
          datalabels: {
            display: true,
            color: (value) => this.setDataLabelColor(value),
            anchor: "end",
            align: "start",
            offset: (value) => Number(this.setOffset(value)),
            formatter: (value, _context) => {
              return value !== 0
                ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                : "";
            },
          },
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
      },
    });
    this.myGroupedChart = groupedChart;
  }

  private setOffset(value): string {
    const dataSet = value.dataset.data[value.dataIndex];
    const minValue = this.totalSum / 15;
    if (dataSet < minValue) {
      return "-20";
    } else {
      return "-10";
    }
  }

  private setDataLabelColor(value) {
    const dataSet = value.dataset.data[value.dataIndex];
    const dataSetColor = value.dataset.backgroundColor[value.dataIndex];
    const minValue = this.totalSum / 15;

    if (dataSet > minValue) {
      if (
        dataSetColor === "#007D8C" ||
        dataSetColor === "#007AB3" ||
        dataSetColor === "#003781"
      ) {
        return "#666666";
      }
    } else {
      return "#414141";
    }
  }
}
